/**
 * User State — shared across the app via React context so login/logout updates
 * Navigation and all consumers immediately without a full page reload.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { userService } from '../service';
import { LOCAL_DEV_USER } from '../service';
import type { User } from '@/shared/types';
import { UserRole } from '@/shared/types';
import { logger } from '@/shared/utils';
import { ENV } from '@/config/environment';
import { entitlementsApiService } from '@/shared/services/entitlementsApiService';
import { CONFIGURABLE_PAGE_KEYS, DEFAULT_ACCESS_TIER, type AccessTier } from '@/shared/config/pageKeys';

export type UserStateContextValue = {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  refreshUser: () => Promise<User | null>;
  updateLocalUser: (updatedUser: User | null) => void;
  hasRole: (role: UserRole) => boolean;
  hasEntitlement: (pageKey: string) => boolean;
};

const UserStateContext = createContext<UserStateContextValue | undefined>(undefined);

export function UserStateProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deniedPageKeys, setDeniedPageKeys] = useState<Set<string>>(new Set());
  const [grantedPageKeys, setGrantedPageKeys] = useState<Set<string>>(new Set());
  const [pageLevels, setPageLevels] = useState<Record<string, AccessTier>>({});

  // Runs once on mount, and is the ONLY thing allowed to decide
  // isAuthenticated: it always calls the server (userService.refreshUser,
  // which hits GET /users/me) rather than trusting the locally-cached
  // token/user cookie on their own. A stale or server-revoked session
  // previously still rendered the app as logged in until something else
  // happened to trigger a refresh - loading stays true, and every
  // protected route shows its loading state, until this real check
  // resolves.
  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      if (ENV.app.localDevMode) {
        logger.info('Local dev mode enabled - using mock admin user', 'UserStateProvider');
        if (!cancelled) {
          setUser(LOCAL_DEV_USER);
          setIsAuthenticated(true);
          setLoading(false);
        }
        return;
      }

      try {
        const validatedUser = await userService.refreshUser();
        if (cancelled) return;

        logger.debug('Initializing user state', 'UserStateProvider', {
          hasUser: !!validatedUser,
          userId: validatedUser?.id,
          username: validatedUser?.username,
        });

        setUser(validatedUser);
        setIsAuthenticated(!!validatedUser);
      } catch (error) {
        if (cancelled) return;
        logger.error('Failed to initialize user state', 'UserStateProvider', { error });
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  // Public - the base public/user/admin tier for every configurable page.
  // Fetched independent of auth state (and before it's known) so a
  // 'public' tier page can render for a logged-out visitor without
  // waiting on the auth check above.
  useEffect(() => {
    if (ENV.app.localDevMode) return;

    let cancelled = false;
    entitlementsApiService.getPageLevels()
      .then((response) => {
        if (cancelled || !response.success || !response.data) return;
        setPageLevels(response.data.levels);
      })
      .catch((error) => {
        logger.warn('Failed to load page access levels - defaulting to admin-only for configurable pages', 'UserStateProvider', { error });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the current user's own entitlement rows once authenticated.
  // Deny rows fail OPEN (an entitlements outage must never be the reason
  // a legitimately-authorized user gets locked out); grant rows
  // effectively fail closed the same way - if this request errors,
  // grantedPageKeys just stays empty and the page's base tier applies,
  // which is the safe direction for an 'admin'-tier per-user override.
  useEffect(() => {
    if (ENV.app.localDevMode || !isAuthenticated) {
      return;
    }

    let cancelled = false;
    entitlementsApiService.getMyEntitlements()
      .then((response) => {
        if (cancelled || !response.success || !response.data) return;
        const denied = response.data.entitlements
          .filter((e) => e.decision === 'deny')
          .map((e) => e.page_key);
        const granted = response.data.entitlements
          .filter((e) => e.decision === 'grant')
          .map((e) => e.page_key);
        setDeniedPageKeys(new Set(denied));
        setGrantedPageKeys(new Set(granted));
      })
      .catch((error) => {
        logger.warn('Failed to load entitlements - defaulting to no denies/grants (fail open on deny)', 'UserStateProvider', { error });
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const refreshUser = useCallback(async (): Promise<User | null> => {
    if (ENV.app.localDevMode) {
      setUser(LOCAL_DEV_USER);
      setIsAuthenticated(true);
      setLoading(false);
      return LOCAL_DEV_USER;
    }

    setLoading(true);

    try {
      const refreshedUser = await userService.refreshUser();
      setUser(refreshedUser);
      setIsAuthenticated(!!refreshedUser);
      return refreshedUser;
    } catch (error) {
      logger.error('Failed to refresh user', 'UserStateProvider', { error });
      setUser(null);
      setIsAuthenticated(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateLocalUser = useCallback((updatedUser: User | null) => {
    if (ENV.app.localDevMode) {
      setUser(updatedUser ?? LOCAL_DEV_USER);
      setIsAuthenticated(true);
      return;
    }

    setUser(updatedUser);
    setIsAuthenticated(!!updatedUser);
  }, []);

  const hasRole = useCallback((role: UserRole): boolean => {
    if (ENV.app.localDevMode) {
      return true;
    }

    return userService.hasRole(role);
  }, []);

  // Two independent layers, matching the backend's page_access decorator:
  //
  // 1. Base tier (CONFIGURABLE_PAGE_KEYS only - dashboard, drafts, profile,
  //    settings, calculator, toastmasters, blog_create/edit/preview):
  //    'public' (anyone) / 'user' (any authenticated user) / 'admin'
  //    (admin/god only). Defaults to 'admin' - blocked for normal users -
  //    until an admin explicitly opens a page up. admin.* pages aren't in
  //    this system at all; they keep their existing hard role gate.
  // 2. Per-user Entitlement override on top: a 'deny' always wins
  //    regardless of tier; a 'grant' lets one specific non-admin user
  //    through an 'admin'-tier page without opening it to everyone.
  const hasEntitlement = useCallback((pageKey: string): boolean => {
    if (ENV.app.localDevMode) {
      return true;
    }

    if (deniedPageKeys.has(pageKey)) {
      return false;
    }

    if (!CONFIGURABLE_PAGE_KEYS.includes(pageKey)) {
      // admin.* pages and anything else outside this system: unchanged,
      // default-allow deny-only behavior (the role gate itself lives in
      // ProtectedRoute's requiredRoles / the backend's @admin_endpoint).
      return true;
    }

    const tier = pageLevels[pageKey] ?? DEFAULT_ACCESS_TIER;
    if (tier === 'public') {
      return true;
    }
    if (!isAuthenticated) {
      return false;
    }
    if (tier === 'user') {
      return true;
    }
    // tier === 'admin'
    const isAdmin = user?.is_admin === true || user?.is_god === true;
    return isAdmin || grantedPageKeys.has(pageKey);
  }, [deniedPageKeys, grantedPageKeys, pageLevels, isAuthenticated, user]);

  const value: UserStateContextValue = {
    user,
    isAuthenticated,
    loading,
    refreshUser,
    updateLocalUser,
    hasRole,
    hasEntitlement,
  };

  return <UserStateContext.Provider value={value}>{children}</UserStateContext.Provider>;
}

export const useUserState = (): UserStateContextValue => {
  const ctx = useContext(UserStateContext);
  if (!ctx) {
    throw new Error('useUserState must be used within UserStateProvider');
  }
  return ctx;
};
